import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';
import { User, Settings, Trophy, Gamepad2, Target, Clock, Save, Trash2 } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [preferences, setPreferences] = useState({
    theme: user?.preferences?.theme || 'auto',
    notifications: user?.preferences?.notifications ?? true,
    language: user?.preferences?.language || 'en'
  });

  const { data: stats, isLoading: statsLoading } = useQuery(
    'userStats',
    () => usersAPI.getStats().then(res => res.data),
    { enabled: !!user }
  );

  const updatePreferencesMutation = useMutation(
    (prefs) => usersAPI.updateProfile({ preferences: prefs }),
    {
      onSuccess: (response) => {
        updateUser({ ...user, preferences: response.data.user.preferences });
        toast.success('Preferences updated successfully!');
        setIsEditing(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update preferences');
      }
    }
  );

  const handleSavePreferences = () => {
    updatePreferencesMutation.mutate(preferences);
  };

  const handleDeleteAccount = () => {
    const confirmPassword = prompt('Type "DELETE" to confirm account deletion:');
    if (confirmPassword === 'DELETE') {
      // Implement account deletion
      toast.error('Account deletion not implemented yet');
    }
  };

  if (statsLoading) {
    return <LoadingSpinner size="xl" />;
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="card">
        <div className="flex items-center space-x-6">
          {user?.avatar ? (
            <img
              src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png`}
              alt={user.username}
              className="h-20 w-20 rounded-full"
            />
          ) : (
            <div className="h-20 w-20 rounded-full bg-discord-blurple flex items-center justify-center">
              <User className="h-10 w-10 text-white" />
            </div>
          )}
          
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-discord-lightest mb-2">
              {user?.username}#{user?.discriminator}
            </h1>
            <p className="text-discord-light mb-2">
              Member since {new Date(user?.createdAt).toLocaleDateString()}
            </p>
            <div className="flex items-center space-x-4">
              <span className={`badge ${
                user?.subscription?.type === 'free' ? '' :
                user?.subscription?.type === 'premium' ? 'badge-primary' :
                user?.subscription?.type === 'pro' ? 'badge-success' :
                'badge-warning'
              }`}>
                {user?.subscription?.type?.charAt(0).toUpperCase() + user?.subscription?.type?.slice(1)} Plan
              </span>
              <span className="text-sm text-discord-light">
                Last login: {new Date(user?.lastLogin).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stats */}
        <div className="lg:col-span-2 space-y-6">
          {/* Game Statistics */}
          <div className="card">
            <h2 className="text-xl font-bold text-discord-lightest mb-4">Game Statistics</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-discord-lightest">
                  {user?.gameStats?.totalGamesPlayed || 0}
                </div>
                <div className="text-sm text-discord-light">Total Games</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-discord-lightest">
                  {user?.gameStats?.winRate || 0}%
                </div>
                <div className="text-sm text-discord-light">Win Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-discord-lightest">
                  {user?.gameStats?.gamesToday || 0}
                </div>
                <div className="text-sm text-discord-light">Games Today</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-discord-lightest">
                  {user?.gameStats?.favoriteGame || 'None'}
                </div>
                <div className="text-sm text-discord-light">Favorite Game</div>
              </div>
            </div>

            {/* Game Type Breakdown */}
            {stats?.gameStats && stats.gameStats.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-discord-lightest mb-3">Game Breakdown</h3>
                <div className="space-y-3">
                  {stats.gameStats.map((gameStat) => (
                    <div key={gameStat.gameType} className="flex items-center justify-between p-3 bg-discord-dark rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Gamepad2 className="h-5 w-5 text-discord-blurple" />
                        <span className="text-discord-lightest font-medium">
                          {gameStat.gameType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-discord-light">
                          {gameStat.totalGames} games
                        </span>
                        <span className="text-discord-light">
                          {gameStat.winRate}% win rate
                        </span>
                        <span className="text-discord-light">
                          {gameStat.avgDuration}s avg
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Recent Games */}
          {stats?.recentGames && stats.recentGames.length > 0 && (
            <div className="card">
              <h2 className="text-xl font-bold text-discord-lightest mb-4">Recent Games</h2>
              <div className="space-y-3">
                {stats.recentGames.slice(0, 5).map((game) => (
                  <div key={game.gameId} className="flex items-center justify-between p-3 bg-discord-dark rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Gamepad2 className="h-5 w-5 text-discord-blurple" />
                      <div>
                        <span className="text-discord-lightest font-medium">
                          {game.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <div className="flex items-center space-x-2 text-sm text-discord-light">
                          <Clock className="h-3 w-3" />
                          <span>{game.duration}s</span>
                          <span>•</span>
                          <span>{new Date(game.completedAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      {game.winner ? (
                        game.winner.userId === user?._id ? (
                          <Trophy className="h-4 w-4 text-discord-yellow" />
                        ) : (
                          <Target className="h-4 w-4 text-discord-light" />
                        )
                      ) : (
                        <span className="text-discord-light">Draw</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Preferences & Settings */}
        <div className="space-y-6">
          {/* Preferences */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-discord-lightest">Preferences</h2>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="btn btn-ghost"
              >
                <Settings className="h-4 w-4" />
              </button>
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-discord-light mb-2">
                    Theme
                  </label>
                  <select
                    value={preferences.theme}
                    onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })}
                    className="input"
                  >
                    <option value="auto">Auto</option>
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-discord-light mb-2">
                    Language
                  </label>
                  <select
                    value={preferences.language}
                    onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
                    className="input"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="notifications"
                    checked={preferences.notifications}
                    onChange={(e) => setPreferences({ ...preferences, notifications: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="notifications" className="text-sm text-discord-light">
                    Enable notifications
                  </label>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={handleSavePreferences}
                    disabled={updatePreferencesMutation.isLoading}
                    className="btn btn-primary flex-1"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-discord-light">Theme:</span>
                  <span className="text-discord-lightest capitalize">
                    {user?.preferences?.theme || 'auto'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-discord-light">Language:</span>
                  <span className="text-discord-lightest">
                    {user?.preferences?.language || 'en'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-discord-light">Notifications:</span>
                  <span className="text-discord-lightest">
                    {user?.preferences?.notifications ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Account Actions */}
          <div className="card">
            <h2 className="text-xl font-bold text-discord-lightest mb-4">Account</h2>
            <div className="space-y-3">
              <button
                onClick={handleDeleteAccount}
                className="btn btn-danger w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
